/**
 * AIChatFAB — Floating Action Button for AI Chat.
 *
 * Shows a floating button in the bottom-right corner of the screen.
 * Only visible on routes where a persona is configured.
 * Clicking opens a slide-up chat panel.
 */

import { useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { useRouterState } from '@tanstack/react-router'
import { useAIPersonasStore } from '@/stores/ai-personas-store'
import { getPersonaForRoute } from '@/lib/services/ai-personas'
import { AIChatPanel } from './AIChatPanel'

export function AIChatFAB() {
  const [isOpen, setIsOpen] = useState(false)
  const routerState = useRouterState()
  const pathname = routerState.location.pathname
  const personas = useAIPersonasStore((s) => s.personas)

  const persona = getPersonaForRoute(pathname, personas)

  // Don't render if no persona matches the current route
  if (!persona) return null

  return (
    <>
      {/* FAB Button */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-brand-primary text-brand-dark shadow-lg hover:bg-brand-primary/90 transition-all hover:scale-105 flex items-center justify-center"
          aria-label={`Abrir ${persona.name}`}
        >
          <Sparkles size={22} />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:bg-transparent sm:pointer-events-none"
            style={{ animation: 'chatPanelFadeIn 0.25s ease-out forwards' }}
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div
            className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[420px] h-[85vh] sm:h-[600px] sm:rounded-xl bg-white border border-gray-200 shadow-2xl flex flex-col overflow-hidden"
            style={{
              animation: 'chatPanelSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3 z-10 p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Fechar chat"
            >
              <X size={16} />
            </button>

            <AIChatPanel persona={persona} />
          </div>
        </>
      )}
    </>
  )
}
